<template>
  <div class="location-status-wrapper" v-if="visible">
    <AppHeaderDropdown right no-caret class="location-status-dropdown">
      <template slot="header">
        <div class="location-status-trigger">
          <AppIcon icon="geo-alt-fill" font-scale="1.25" weight="450"></AppIcon>
          <span class="location-status-dot" :class="'location-status-dot--' + locationPermission"></span>
        </div>
      </template>
      <template slot="dropdown">
        <div class="location-status-panel" @click.stop>
          <div class="location-status-panel-title">
            <AppIcon icon="geo-alt-fill" class="mr-1"></AppIcon> Ubicación GPS
          </div>

          <div class="location-status-row">
            <span class="location-status-label">Permiso</span>
            <b-badge :variant="locationPermissionVariant">{{ locationPermissionLabel }}</b-badge>
          </div>
          <div class="location-status-hint">{{ locationPermissionTooltip }}</div>

          <div class="location-status-row mt-2">
            <span class="location-status-label">Último envío</span>
            <b-badge :variant="locationLastSentVariant">{{ locationLastSentLabel }}</b-badge>
          </div>
          <div class="location-status-hint">{{ locationLastSentTooltip }}</div>
        </div>
      </template>
    </AppHeaderDropdown>
  </div>
</template>

<script>
import Helper from '@/handler/helper'
import LocationTracker from '@/handler/locationTracker'
import { HeaderDropdown as AppHeaderDropdown } from '@coreui/vue'
import moment from 'moment'

export default {
  name: 'LocationStatusIndicator',
  components: {
    AppHeaderDropdown,
  },
  data() {
    return {
      locationPermission: 'unknown',
      locationLastSentAt: 0,
      locationLastSentTick: 0,
      locationLastSentTimer: null,
    }
  },
  computed: {
    visible() {
      return !!Helper.getSeller()
    },
    locationPermissionLabel() {
      switch (this.locationPermission) {
        case 'granted':
          return 'Permitido'
        case 'denied':
          return 'Denegado'
        case 'prompt':
          return 'Pendiente'
        case 'unsupported':
          return 'No soportado'
        default:
          return 'Desconocido'
      }
    },
    locationPermissionVariant() {
      switch (this.locationPermission) {
        case 'granted':
          return 'success'
        case 'denied':
          return 'danger'
        case 'prompt':
          return 'warning'
        case 'unsupported':
          return 'secondary'
        default:
          return 'dark'
      }
    },
    locationPermissionTooltip() {
      switch (this.locationPermission) {
        case 'granted':
          return 'Permiso de ubicación concedido'
        case 'denied':
          return 'Permiso de ubicación denegado'
        case 'prompt':
          return 'Permiso de ubicación pendiente'
        case 'unsupported':
          return 'El navegador no soporta geolocalización'
        default:
          return 'Estado de permisos desconocido'
      }
    },
    locationLastSentLabel() {
      if (!this.locationLastSentAt) {
        return 'Sin envíos'
      }

      const now = this.locationLastSentTick || Date.now()
      const diffMs = Math.max(0, now - this.locationLastSentAt)
      const diffMin = Math.floor(diffMs / 60000)

      if (diffMin <= 0) {
        return 'Recién'
      }
      if (diffMin < 60) {
        return `Hace ${diffMin}m`
      }

      const hours = Math.floor(diffMin / 60)
      const minutes = diffMin % 60
      if (hours < 24) {
        return `Hace ${hours}h ${minutes}m`
      }

      const days = Math.floor(hours / 24)
      const remHours = hours % 24
      return `Hace ${days}d ${remHours}h`
    },
    locationLastSentVariant() {
      return this.locationLastSentAt ? 'info' : 'secondary'
    },
    locationLastSentTooltip() {
      if (!this.locationLastSentAt) {
        return 'Aún no hay envíos registrados'
      }

      if (typeof moment === 'function') {
        return 'Último envío: ' + moment(this.locationLastSentAt).format('DD/MM/YYYY HH:mm')
      }

      const date = new Date(this.locationLastSentAt)
      return 'Último envío: ' + date.toLocaleString()
    },
  },
  mounted() {
    this.initLocationTracker()
  },
  beforeDestroy() {
    if (this.locationLastSentTimer) {
      clearInterval(this.locationLastSentTimer)
      this.locationLastSentTimer = null
    }

    window.removeEventListener('seller-location-permission', this.onLocationPermission)
    window.removeEventListener('seller-location-last-sent', this.onLocationLastSent)
  },
  methods: {
    initLocationTracker() {
      if (this.visible) {
        LocationTracker.start()
        this.refreshLocationPermission()
        this.locationLastSentAt = LocationTracker.getLastSent ? LocationTracker.getLastSent() : 0
        this.locationLastSentTick = Date.now()
        this.startLocationLastSentTicker()
        window.addEventListener('seller-location-permission', this.onLocationPermission)
        window.addEventListener('seller-location-last-sent', this.onLocationLastSent)
      }
    },
    refreshLocationPermission() {
      LocationTracker.checkPermission().then((state) => {
        this.locationPermission = state || 'unknown'
      })
    },
    onLocationPermission(event) {
      if (event && event.detail && event.detail.state) {
        this.locationPermission = event.detail.state
      } else {
        this.locationPermission = 'unknown'
      }
    },
    onLocationLastSent(event) {
      if (event && event.detail && event.detail.timestamp) {
        this.locationLastSentAt = event.detail.timestamp
        this.locationLastSentTick = Date.now()
      }
    },
    startLocationLastSentTicker() {
      if (this.locationLastSentTimer) {
        clearInterval(this.locationLastSentTimer)
      }

      this.locationLastSentTimer = setInterval(() => {
        this.locationLastSentTick = Date.now()
      }, 60000)
    },
  },
}
</script>

<style scoped>
.location-status-trigger {
  position: relative;
  width: 34px;
  height: 34px;
  margin: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--app-color-icon);
  transition: background-color 0.15s ease;
}
.location-status-trigger:hover {
  background-color: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.location-status-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--app-color-surface);
}
.location-status-dot--granted {
  background: var(--app-color-success);
}
.location-status-dot--denied {
  background: var(--app-color-danger);
}
.location-status-dot--prompt {
  background: var(--app-color-warning);
}
.location-status-dot--unsupported,
.location-status-dot--unknown {
  background: var(--app-color-text-subtle);
}

.location-status-panel {
  /* Sin width propio: el ancho lo define únicamente el .dropdown-menu
     (min-width en el bloque sin scope, mismo patrón que NotificationsBell/
     MessagesWidget). Si este div fija su propio width en px, compite con
     el width:auto que Popper.js calcula para el <ul>.dropdown-menu (queda
     sobre-restringido con left+right+width simultáneos) y el <ul> puede
     terminar más angosto que este panel: el fondo blanco sólo pinta el
     ancho del <ul>, y el resto del contenido queda "flotando" sin caja y
     recortado por el borde de la ventana. Con width:100% no hay conflicto
     posible: el panel siempre mide exactamente lo que mida el dropdown. */
  width: 100%;
  box-sizing: border-box;
  padding: 0.9rem 1rem;
}
.location-status-panel-title {
  font-weight: 700;
  margin-bottom: 0.6rem;
  white-space: nowrap;
}
.location-status-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}
.location-status-label {
  color: var(--app-color-text-muted);
  font-size: 0.85rem;
  margin-right: 0.5rem;
  white-space: nowrap;
}
.location-status-hint {
  color: var(--app-color-text-subtle);
  font-size: 0.75rem;
  margin-top: 0.15rem;
  white-space: normal;
}
</style>

<style>
/* .dropdown-menu lo genera AppHeaderDropdown puertas adentro (no es
   contenido de este componente), así que un <style scoped> nunca lo
   alcanza: por eso el mismo patrón se repite en NotificationsBell/
   MessagesWidget/RemindersQuickAdd.
   Causa real del recorte (confirmado con getComputedStyle en vivo, no es
   un tema de overflow): bootstrap-vue arma este dropdown con Popper.js,
   que fija por inline style `left:0` + `transform: translate3d(...)`. La
   clase `.dropdown-menu-right` (agregada por el prop `right`) además fija
   `right:0` por CSS. Con left Y right seteados a la vez y `width:auto`,
   el ancho del <ul> queda sobre-restringido y el navegador lo resuelve a
   un valor angosto e inconsistente según el contenido — el <ul> terminaba
   más angosto que su contenido real, así que el fondo blanco no cubría
   todo el panel y lo que sobraba quedaba sin caja y cortado por el borde
   de la ventana. Fix: anular el `right` heredado y fijar el ancho con
   min-width (mismo mecanismo que ya usan NotificationsBell/MessagesWidget)
   en vez de `width:auto`, para que deje de haber dos restricciones
   horizontales compitiendo. */
.location-status-dropdown .dropdown-menu {
  right: auto;
  min-width: 260px;
  width: 260px;
  height: auto;
  max-height: none;
  overflow: visible;
  padding: 0;
  background: var(--app-color-surface);
  box-shadow: var(--app-shadow-md);
}
/* Mismo conector que RemindersQuickAdd.vue/MessagesWidget.vue/
   NotificationsBell.vue, por consistencia visual entre las burbujas del
   header — acá el panel ya quedaba razonablemente cerca del ícono (ancho
   fijo más chico), pero sin ningún indicador visual que lo conecte. */
.location-status-dropdown.show::after {
  content: '';
  position: absolute;
  top: 28px;
  right: 21px;
  width: 12px;
  height: 12px;
  background: var(--app-color-surface);
  transform: rotate(45deg);
  box-shadow: -1px -1px 3px rgba(11, 31, 59, 0.08);
  z-index: 1001;
  pointer-events: none;
}
</style>
